import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

// Diálogo a pantalla completa que entra desde abajo (antes: Dialog fullScreen + Slide de Material UI, con sus tiempos).
// Usa <dialog> con showModal(): queda por encima de todo, deja el resto de la página inerte y cierra con Escape.
const ENTER_MS = 225;
const EXIT_MS = 195;

const slideIn = keyframes`
  from { transform: translateY(100%); }
  to { transform: none; }
`;

const slideOut = keyframes`
  from { transform: none; }
  to { transform: translateY(100%); }
`;

const Sheet = styled.dialog`
  box-sizing: border-box;
  width: 100vw;
  height: 100vh;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: #fff;
  overflow-y: auto;
  overscroll-behavior: contain;
  animation: ${slideIn} ${ENTER_MS}ms cubic-bezier(0, 0, 0.2, 1);
  &[data-closing="true"] {
    animation: ${slideOut} ${EXIT_MS}ms cubic-bezier(0.4, 0, 1, 1) forwards;
  }
  &::backdrop {
    background: rgba(0, 0, 0, 0.5);
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    &[data-closing="true"] {
      animation: none;
    }
  }
`;

export default function FullScreenDialog({ open, onClose, label, children }) {
  const dialog = useRef(null);
  // Sigue montado mientras corre la animación de salida.
  const [mounted, setMounted] = useState(open);
  if (open && !mounted) setMounted(true);
  const closing = mounted && !open;

  useLayoutEffect(() => {
    const node = dialog.current;
    if (open && node && !node.open) node.showModal?.();
  }, [open, mounted]);

  useEffect(() => {
    if (!closing) return undefined;
    const timer = setTimeout(() => {
      dialog.current?.close?.();
      setMounted(false);
    }, EXIT_MS);
    return () => clearTimeout(timer);
  }, [closing]);

  if (!mounted) return null;
  return (
    <Sheet
      ref={dialog}
      aria-label={label}
      data-closing={closing}
      // Escape cierra a través del padre, para que también haya animación de salida.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      {children}
    </Sheet>
  );
}
